import { ReactElement } from "react";
import Head from "next/head";
import Image from "next/image";
import { MainLayout } from "@/containers/layout/LandingLayout";
import StillHaveQuestions from "@/components/faqs/StillHaveQuestions/StillHaveQuestions";
import KnowYourBudget from "@/components/faqs/KnowYourBudget/KnowYourBudget";
import ArticleList from "@/components/blog/articleList/ArticleList";
import { Props } from "@/components/landlords/components/types";
import getConfig from "next/config";
import routes from "@/constants/routes";

const { publicRuntimeConfig } = getConfig();

const Blog = ({ theme = "light" }: Props): JSX.Element => {
  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="canonical" href="https://www.keyzy.com/blog" />
        <meta name="robots" content="index, follow" />

        <title>Keyzy Blog | Rent To Buy Homes & UK Housing Market</title>
        <meta
          name="description" key="description"
          content="Articles covering rent to buy, home ownership, alternative mortgages and customer stories. Read analysis and commentary from Keyzy on the UK property market."
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.keyzy.com/blog" key="ogurl" />
        <meta
          property="og:title"
          content="Keyzy Blog | Rent To Buy Homes & UK Housing Market"
          key="ogtitle"
        />
        <meta property="og:image" content="/blog/meta-og-blog.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Keyzy Blog - Rent To Buy Homes & UK Housing Market"
        />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Articles covering rent to buy, home ownership, alternative mortgages and customer stories. Read analysis and commentary from Keyzy on the UK property market."
          key="ogdesc"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" key="twcard" />
        <meta name="twitter:site" content="@keyzyhomes" key="twsite" />
        <meta name="twitter:creator" content="@keyzyhomes" key="twhandle" />
        <meta
          name="twitter:title"
          content="Keyzy Blog | Rent To Buy Homes & UK Housing Market"
          key="twtitle"
        />
        <meta
          name="twitter:description"
          content="Articles covering rent to buy, home ownership, alternative mortgages and customer stories. Read analysis and commentary from Keyzy on the UK property market."
          key="twdescription"
        />
        <meta name="twitter:image" content="/blog/meta-og-blog.png" key="twimage" />
        <meta
          name="twitter:image:alt"
          content="Keyzy Blog - Rent To Buy Homes & UK Housing Market"
        />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta
          property="og:title"
          content="Keyzy Blog | Rent To Buy Homes & UK Housing Market"
          key="ogtitle"
        />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Articles covering rent to buy, home ownership, alternative mortgages and customer stories. Read analysis and commentary from Keyzy on the UK property market."
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://keyzy.com/" },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://keyzy.com/blog" },
              ],
            }),
          }}
        />
      </Head>

      <div className="bg-white">
        <div className="relative text-center md:mb-14 mt-8">
          <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>

          <h1 className="md:px-6 px-5 lg:px-0 xl:px-0 xl:w-1/2 mx-auto mb-10 font-bold text-4xl xs:text-[44px] md:text-[56px] xl:text-[76px] xs:leading-[46px] md:leading-[59px] xl:leading-[80px]">
            Keyzy Blog
          </h1>
          <h2 className="md:px-6 px-5 lg:px-0 xl:px-0 font-serif leading-normal text-lg lg:text-[26px] leading-[39px] max-w-[500px] lg:max-w-[750px] mx-auto">
            Articles and stories about rent to buy homes and how Keyzy is making a difference to
            first time buyers’ lives
          </h2>

          <div className="absolute hidden -right-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles 2"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>
        </div>
      </div>
      <ArticleList
        baseUrl={routes.blog}
        excludeCategories={publicRuntimeConfig.NEXT_BLOG_LANDLORD_CATEGORY_ID}
      />
      <div className="left-0 right-0 overflow-hidden bg-repeat-x h-24 md:h-32 xl:h-48 bg-[url('/intermediaries/city-outline-mobile.svg')]  md:bg-[url('/intermediaries/city-outline-tablet.svg')] xl:bg-[url('/intermediaries/city-outline-desktop.svg')]" />
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-1/2">
          <StillHaveQuestions />
        </div>
        <div className="lg:w-1/2">
          <KnowYourBudget />
        </div>
      </div>
    </>
  );
};

Blog.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};

export default Blog;
